/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useQuery } from '@tanstack/react-query';

import type { GetTenantReleasesResponse, TenantReleaseListItem } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { semverCompare } from '@/utils/semverCompare';

export type ReleasesByOS = {
  linux?: ReleasesByOsResult;
  windows?: ReleasesByOsResult;
};

export type ReleasesByOsResult = {
  latest: TenantReleaseListItem | null;
  previousVersions: TenantReleaseListItem[];
  allReleases: TenantReleaseListItem[];
};

export type UseTenantReleasesQueryParams = {
  osName?: string;
  version?: string;
};

/**
 * Custom hook that fetches tenant releases and groups them by operating system.
 *
 * Returns releases grouped by OS (Linux/Windows) with latest/previous versions breakdown for each OS.
 * The osName and version parameters are used for query key differentiation.
 *
 * @param options Configuration object:
 *   - osName: Optional OS name to filter results ('linux' or 'windows')
 *   - version: Optional specific version to highlight (e.g., 'v1.5.8')
 *
 * @returns Object containing:
 *   - data: ReleasesByOS with latest/previous versions for each OS
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - refetch: Function to manually refetch data
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export function useTenantReleasesQuery({ osName, version }: UseTenantReleasesQueryParams = {}) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const query = useQuery({
    queryKey: osName
      ? ['tenant-releases-by-os', selectedTenantId, osName, version]
      : ['tenant-releases', selectedTenantId],
    queryFn: async (): Promise<ReleasesByOS> => {
      if (!selectedTenantId) {
        // Return empty ReleasesByOS structure
        return {};
      }

      const filters: Record<string, string> = { legacy: 'false' };
      if (osName) {
        filters.targetOs = osName;
      }

      const response: GetTenantReleasesResponse = await mgmtPlaneAPIClient.tenantReleases.getTenantReleases({
        tenantId: selectedTenantId,
        filters,
        pageSize: 100,
      });

      // Helper function to create ReleasesByOsResult for an OS
      const createOsResult = (releases: TenantReleaseListItem[], targetVersion?: string): ReleasesByOsResult => {
        if (releases.length === 0) {
          return {
            latest: null,
            previousVersions: [],
            allReleases: [],
          };
        }

        // Sort by version, highest first using semver comparison
        const sortedReleases = releases.sort((a, b) => semverCompare(b.version ?? '', a.version ?? ''));

        if (targetVersion) {
          // If a specific version is requested, find it
          const specificRelease = sortedReleases.find((release) => release.version === targetVersion);
          const otherVersions = sortedReleases.filter((release) => release.version !== targetVersion);

          return {
            latest: specificRelease || null,
            previousVersions: otherVersions,
            allReleases: sortedReleases,
          };
        }

        // Otherwise, return latest and previous versions
        const [latest, ...previousVersions] = sortedReleases;

        return {
          latest,
          previousVersions,
          allReleases: sortedReleases,
        };
      };

      // Group releases by OS and apply business rules
      const linuxReleases: TenantReleaseListItem[] = [];
      const windowsReleases: TenantReleaseListItem[] = [];

      response.data.forEach((release) => {
        const osKey = release.targetOs?.toLowerCase();
        if (osKey === 'linux') {
          linuxReleases.push(release);
        } else if (osKey === 'windows') {
          windowsReleases.push(release);
        }
      });

      // Build the ReleasesByOS structure
      const releasesByOS: ReleasesByOS = {};

      if (linuxReleases.length > 0) {
        releasesByOS.linux = createOsResult(linuxReleases, osName === 'linux' ? version : undefined);
      }

      if (windowsReleases.length > 0) {
        releasesByOS.windows = createOsResult(windowsReleases, osName === 'windows' ? version : undefined);
      }

      return releasesByOS;
    },
    enabled: true,
    staleTime: 10 * 1000, // 10 seconds
  });

  return {
    data: query.data as ReleasesByOS | undefined,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
